import { useState } from 'react';
import {
    Badge,
    BlockStack,
    Box,
    Button,
    Card,
    Disclosure,
    InlineStack,
    Modal,
    SegmentedControl,
    TextField,
    Typography,
    type BadgeTone,
    type SegmentedOption,
} from '@pv-frontend/ui-components-web';

/**
 * Plain view model for a feature flag — deliberately decoupled from the Relay
 * generated types so this presentational component is unit-testable without a
 * Relay harness or the Babel transform. The screen maps query data into it.
 *
 * Mirrors the Admin API's `FeatureFlagState` three-dial model: rollout stage,
 * kill switch, GA percentage ramp, plus the flag's kind. Enum values arrive as
 * strings so a `%future added value` from Relay degrades gracefully.
 */
export interface FeatureFlagView {
    key: string;
    stage: string;
    killed: boolean;
    percent: number;
    kind: string;
}

/** The operational dials the list drives, wired by the screen to `Apis.Admin`. */
export interface FeatureFlagActions {
    onSetStage: (key: string, stage: string) => void;
    onSetKilled: (key: string, killed: boolean) => void;
    onSetPercent: (key: string, percent: number) => void;
}

export interface FeatureFlagListProps extends Partial<FeatureFlagActions> {
    flags: readonly FeatureFlagView[];
    /** Keys with a mutation in flight — their dials are disabled meanwhile. */
    busyKeys?: ReadonlySet<string>;
    /** Keys whose rows start expanded (dev preview / tests). */
    defaultExpanded?: readonly string[];
}

const STAGE_OPTIONS: readonly SegmentedOption<string>[] = [
    { value: 'OFF', label: 'Off' },
    { value: 'INTERNAL', label: 'Internal' },
    { value: 'BETA', label: 'Beta' },
    { value: 'GA', label: 'GA' },
];

const STAGE_LABELS: Record<string, string> = {
    OFF: 'Off',
    INTERNAL: 'Internal',
    BETA: 'Beta',
    GA: 'GA',
};

const STAGE_TONES: Record<string, BadgeTone> = {
    OFF: 'neutral',
    INTERNAL: 'primary',
    BETA: 'warning',
    GA: 'success',
};

const KIND_LABELS: Record<string, string> = {
    RELEASE: 'Release',
    OPS: 'Ops',
    EXPERIMENT: 'Experiment',
};

function StageBadge({ flag }: { flag: FeatureFlagView }) {
    if (flag.killed) {
        return (
            <Badge tone="critical" dot testID={`status-${flag.key}`}>
                Killed
            </Badge>
        );
    }

    const label =
        flag.stage === 'GA' && flag.percent < 100
            ? `GA · ${flag.percent}%`
            : (STAGE_LABELS[flag.stage] ?? flag.stage);

    return (
        <Badge tone={STAGE_TONES[flag.stage] ?? 'neutral'} dot testID={`status-${flag.key}`}>
            {label}
        </Badge>
    );
}

/** Validate a raw percent input: an integer 0–100. Returns `null` when valid. */
function percentError(raw: string): string | null {
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) return 'Enter a whole number.';
    const value = Number(trimmed);
    return value >= 0 && value <= 100 ? null : 'Must be 0–100.';
}

interface PercentDialProps {
    flag: FeatureFlagView;
    busy: boolean;
    onSetPercent: FeatureFlagActions['onSetPercent'];
}

/** The GA ramp input + Set affordance. Rendered only while the flag is at GA. */
function PercentDial({ flag, busy, onSetPercent }: PercentDialProps) {
    const [percentInput, setPercentInput] = useState(String(flag.percent));
    const error = percentError(percentInput);
    const dirty = percentInput.trim() !== String(flag.percent);
    const submit = () => {
        if (!error) onSetPercent(flag.key, Number(percentInput.trim()));
    };

    return (
        <Box minWidth={200}>
            <InlineStack gap="200" blockAlign="end" wrap={false}>
                <Box flex={1}>
                    <TextField
                        label="GA ramp %"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        value={percentInput}
                        disabled={busy}
                        error={dirty ? (error ?? undefined) : undefined}
                        onChange={setPercentInput}
                        onSubmitEditing={submit}
                    />
                </Box>
                <Button size="slim" variant="block" disabled={busy || !!error || !dirty} onPress={submit}>
                    Set
                </Button>
            </InlineStack>
        </Box>
    );
}

interface FlagRowProps {
    flag: FeatureFlagView;
    actions: FeatureFlagActions;
    busy: boolean;
    first: boolean;
    defaultExpanded: boolean;
    onRequestKill: (key: string) => void;
}

/**
 * One flag: a compact, scannable summary row (key + kind, status badge), with
 * the Stage / GA-ramp / Kill dials revealed on expand. The ramp dial only
 * exists at GA — it is meaningless at any other stage — and a killed flag's
 * dials are disabled so Unkill is the only way back.
 */
function FlagRow({ flag, actions, busy, first, defaultExpanded, onRequestKill }: FlagRowProps) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const dialsDisabled = busy || flag.killed;

    return (
        <Box borderColor="border-card" borderBlockStartWidth={first ? undefined : '0165'}>
            <Disclosure
                open={expanded}
                onToggle={() => setExpanded((current) => !current)}
                testID={`row-${flag.key}`}
                summary={
                    <InlineStack align="space-between" blockAlign="center" gap="300">
                        <BlockStack gap="050">
                            <Typography variant="body-md" fontWeight="semibold">
                                {flag.key}
                            </Typography>
                            <Typography variant="body-sm" tone="text-secondary">
                                {KIND_LABELS[flag.kind] ?? flag.kind}
                            </Typography>
                        </BlockStack>
                        <StageBadge flag={flag} />
                    </InlineStack>
                }
            >
                <Box paddingInline="400" paddingBlockStart="100" paddingBlockEnd="400">
                    <InlineStack align="space-between" blockAlign="end" gap="400" wrap>
                        <BlockStack gap="150">
                            <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                                Stage
                            </Typography>
                            <SegmentedControl
                                accessibilityLabel={`Rollout stage for ${flag.key}`}
                                options={STAGE_OPTIONS}
                                value={flag.stage}
                                disabled={dialsDisabled}
                                onChange={(stage) => actions.onSetStage(flag.key, stage)}
                            />
                        </BlockStack>

                        {flag.stage === 'GA' && !flag.killed ? (
                            <PercentDial flag={flag} busy={busy} onSetPercent={actions.onSetPercent} />
                        ) : null}

                        {flag.killed ? (
                            <Button
                                size="slim"
                                variant="block"
                                disabled={busy}
                                onPress={() => actions.onSetKilled(flag.key, false)}
                            >
                                Unkill
                            </Button>
                        ) : (
                            <Button
                                size="slim"
                                variant="critical"
                                disabled={busy}
                                onPress={() => onRequestKill(flag.key)}
                            >
                                Kill
                            </Button>
                        )}
                    </InlineStack>
                </Box>
            </Disclosure>
        </Box>
    );
}

const noop = () => undefined;

/** Card of compact flag rows; each expands into its Stage / Kill / Percent dials. */
export function FeatureFlagList({
    flags,
    onSetStage = noop,
    onSetKilled = noop,
    onSetPercent = noop,
    busyKeys,
    defaultExpanded,
}: FeatureFlagListProps) {
    const [confirmKillKey, setConfirmKillKey] = useState<string | null>(null);
    const actions: FeatureFlagActions = { onSetStage, onSetKilled, onSetPercent };

    if (flags.length === 0) {
        return (
            <Card>
                <Typography tone="text-secondary">No feature flags are defined yet.</Typography>
            </Card>
        );
    }

    return (
        <>
            <Card padding="0">
                {flags.map((flag, index) => (
                    <FlagRow
                        key={flag.key}
                        flag={flag}
                        actions={actions}
                        busy={busyKeys?.has(flag.key) ?? false}
                        first={index === 0}
                        defaultExpanded={defaultExpanded?.includes(flag.key) ?? false}
                        onRequestKill={setConfirmKillKey}
                    />
                ))}
            </Card>

            <Modal
                open={confirmKillKey !== null}
                onClose={() => setConfirmKillKey(null)}
                title={`Kill ${confirmKillKey ?? ''}?`}
                actions={
                    <>
                        <Button size="slim" variant="tertiary" onPress={() => setConfirmKillKey(null)}>
                            Cancel
                        </Button>
                        <Button
                            size="slim"
                            variant="block"
                            onPress={() => {
                                if (confirmKillKey) onSetKilled(confirmKillKey, true);
                                setConfirmKillKey(null);
                            }}
                        >
                            Kill flag
                        </Button>
                    </>
                }
            >
                <Typography tone="text-secondary">
                    Killing turns this flag off for every vault within ~30 seconds — no deploy needed. You can
                    unkill it again at any time.
                </Typography>
            </Modal>
        </>
    );
}
